/* eslint-disable no-underscore-dangle, class-methods-use-this */

import Cookies from 'js-cookie';
import tags from './tags';

const { country, countryRegion, trackingInfo: { onetrustId } } = tags;

// array of GDPR countries.
const gdprMembers = [
  'AT', 'BE', 'BG', 'BR', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'LI', 'IS', 'NO',
];

// array of LGPD countries
const lgpdMembers = [
  'BR',
];

// array of CCPA states
const ccpaMembers = ['CA', 'California', 'CO', 'Colorado', 'CT', 'Connecticut', 'VA', 'Virginia'];

// array of countries requiring imprint / impressum page
const imprintMembers = ['DE', 'JP'];

/**
 * Determines if impressum / imprint is quired
 *
 * @param countryCode The country code to check against.
 * @returns {boolean} Whether or not an imprint / impressum page is required for the country code.
 */
const isImprint = (countryCode) => {
  const url = document.URL;
  if (url.includes('force=imprint')) return true;

  return imprintMembers.includes(countryCode);
};

/**
 * Determines if GDPR should apply
 *
 * @param countryCode
 * @returns {boolean}
 */
const isGdpr = (countryCode) => {
  const url = document.URL;
  if (url.includes('debug=gdpr')) return true;

  return gdprMembers.includes(countryCode);
};

/**
 * Determines if LGPD should apply
 *
 * @param countryCode
 * @returns {boolean}
 */
const isLgpd = (countryCode) => {
  const url = document.URL;
  if (url.includes('debug=lgpd')) return true;

  return lgpdMembers.includes(countryCode);
};

/**
 * Determines if CCPA should apply
 *
 * @param countryCode
 * @param countryRegion
 * @returns {boolean}
 */
// eslint-disable-next-line no-shadow
const isCcpa = (countryCode, countryRegion) => {
  const url = document.URL;
  if (url.includes('debug=ccpa')) return true;

  return countryCode === 'US' && ccpaMembers.includes(countryRegion);
};

/**
 * Get country when set via query parameters.
 */
const getGeoCountryFromQueryParam = () => {
  const queryParams = new URLSearchParams(window.location.search);

  if (queryParams.has('debug') && queryParams.has('otgeo')) {
    window.s1.blockInjector = window.s1?.blockInjector || {};
    window.s1.markupModifier = window.s1?.markupModifier || {};

    const userData = window?.s1?.markupModifier || window?.s1?.blockInjector || {};

    userData.country = queryParams.get('otgeo');
  }
};

/**
 * CMP Class
 */
class Cmp {
  // This will be a queue of functions to run.
  queue = [];

  // This is variable that will ensure that runQueue only runs once.
  queueInitialized = false;

  /**
   * This will run when the MCP class is instantiated.
   */
  constructor() {
    // (boolean) true if GDPR should apply
    this.gdprApplies = isGdpr(country);

    // (boolean) true if LGPD should apply
    this.lgpdApplies = isLgpd(country);

    // (boolean) true if CCPA should apply
    this.ccpaApplies = isCcpa(country, countryRegion);

    // (boolean) whether imprint should apply
    this.imprintApplies = isImprint(country);

    // set country when debug param and otgeo is present.
    getGeoCountryFromQueryParam();

    if (this.gdprApplies || this.lgpdApplies || this.ccpaApplies) {
      this.loadCmp();

      if (Cookies.get('gdpr_opt_in')) {
        if (Cookies.get('gdpr_opt_in') === '1') {
          this.runQueue(1);
        } else {
          this.runQueue(0);
        }
      } else if (this.ccpaApplies) {
        Cookies.set('gdpr_opt_in', '1');
        this.runQueue(1);
      } else if (this.gdprApplies || this.lgpdApplies) {
        Cookies.set('gdpr_opt_in', '0');
        this.runQueue(0);
      }
    } else {
      this.runQueue(1);
    }

    window.s1 = window.s1 || {};
    window.s1.CmpWrapper = this;
  }

  /**
   * Initializes the CMP
   */
  loadCmp() {
    this.loadOnetrustScript();
    if (this.ccpaApplies) {
      this.loadOnetrustUspapi();
    }

    /**
     * Callback function that is called when Onetrust has successfully been initialized.
     */
    window.OptanonWrapper = () => {
      /**
       * Fires the appropriate event, base on consent when consent has changed.
       */
      window.OneTrust.OnConsentChanged(() => {
        // Set gdpr_opt_in cookie
        if (window.OnetrustActiveGroups.includes('C0002') && window.OnetrustActiveGroups.includes('C0003') && window.OnetrustActiveGroups.includes('C0004')) {
          Cookies.set('gdpr_opt_in', '1');
        } else {
          Cookies.set('gdpr_opt_in', '0');
        }
        // force reload.
        window.location.reload();
      });
    };
  }

  /**
   * Load Onetrust's main script
   */
  loadOnetrustScript() {
    const newScript = document.createElement('script');
    newScript.type = 'text/javascript';
    newScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    newScript.charset = 'UTF-8';
    newScript.dataset.domainScript = onetrustId;

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(newScript, firstScript);
  }

  /**
   * Load Onetrust USPAPI script.
   */
  loadOnetrustUspapi() {
    const newScript = document.createElement('script');
    newScript.type = 'text/javascript';
    newScript.src = 'https://cdn.cookielaw.org/opt-out/otCCPAiab.js';
    newScript.charset = 'UTF-8';
    newScript.setAttribute('ccpa-opt-out-ids', 'C0004');
    newScript.setAttribute('ccpa-opt-out-geo', 'all');
    newScript.setAttribute('ccpa-opt-out-lspa', 'false');

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(newScript, firstScript);
  }

  /**
   * Enqueue functions that will wait for the CMP consent to be set. Enqueue takes in 2 arguments,
   * The function to run and the consent level when the function can run.
   *
   * @param functionCall the function to run
   * @param consentLevel the consent level when to run the function. 1 is full consent and 0 is
   *   no consent.
   */
  enqueue(functionCall, consentLevel = 1) {
    this.queue.push([functionCall, consentLevel]);
  }

  /**
   * Initializes the queue and run all the functions whose consent level matches.
   * This method can only be run once.
   *
   * @param consentLevel the consent level when to run the function. 1 is full consent and 0 is no
   *   consent.
   */
  runQueue(consentLevel = 1) {
    if (!this.queueInitialized) {
      console.log('CMP queue starting');
      this.queueInitialized = true;
    } else {
      console.log('CMP queue already intialized');
      return;
    }

    // excecute the queue.
    this.queue.forEach((element) => {
      const [f, consent] = element;
      if (consent === consentLevel) {
        f();
      }
    });

    // run all subsequent functions that are added to the queue directly.
    this.queue._push = this.queue.push;
    this.queue.push = (element) => {
      const [f, consent] = element;
      if (!!(f && f.constructor && f.call && f.apply) && consent === consentLevel) {
        this.queue._push(f);
        f();
      }
    };
  }

  /**
   * Will trigger the Banner for the respective Privacy Policy.
   */
  showCmpBanner() {
    window.OneTrust.ToggleInfoDisplay();
  }
}

// export the CMP class as a singleton (can only be instantiated once).
export default new Cmp();
